// src/Pages/AiTrading/TradesTable.js
import React, { useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box, Tabs, Tab, Grid, Card, CardContent, Typography, Button,
  IconButton, styled, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Divider, CircularProgress
} from '@mui/material';
import {
  TrendingUp as ProfitIcon,
  TrendingDown as LossIcon,
  ArrowUpward,
  ArrowDownward,
  ShareOutlined as ShareIcon,
  FileDownloadOutlined as DownloadIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import {
  stopTrade,
  cancelRemainingTrades,
  fetchTradeImage,
  clearTradeImage
} from '../../store/Slices/aiTradingSlice';
import { fetchAIBalances } from '../../store/Slices/assetSlice';

// ===== helpers =====
const isString = (v) => typeof v === 'string';
const looksLikeDataUri = (s) => isString(s) && s.startsWith('data:image');
const looksLikeHttp = (s) => isString(s) && /^https?:\/\//i.test(s);
const looksLikeBase64 = (s) => isString(s) && /^[A-Za-z0-9+/=]+$/.test(s) && s.length > 100;

const extractStringFromAny = (val) => {
  if (!val) return '';
  if (isString(val)) return val;
  if (val.image) return extractStringFromAny(val.image);
  if (val.data) return extractStringFromAny(val.data);
  if (val.url) return extractStringFromAny(val.url);
  if (val.base64) return extractStringFromAny(val.base64);
  return '';
};

const normalizeImageUri = (val) => {
  const s = extractStringFromAny(val);
  if (!s) return '';
  if (looksLikeDataUri(s) || looksLikeHttp(s)) return s;
  if (looksLikeBase64(s)) return `data:image/png;base64,${s}`;
  return '';
};

const fmtSmart = (n) => {
  const v = Number(n ?? 0);
  if (!isFinite(v)) return '0';
  if (Math.abs(v) >= 1) return v.toFixed(2);
  return v.toFixed(8).replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
};

// ===== styles =====
const ScrollableContainer = styled(Box)(({ theme, light }) => ({
  maxHeight: '600px',
  overflowY: 'auto',
  padding: '4px',
  '&::-webkit-scrollbar': { width: '1px' },
  '&::-webkit-scrollbar-thumb': {
    background: light ? theme.palette.grey[400] : theme.palette.grey[600],
    borderRadius: '4px'
  },
}));

const DirectionBox = styled(Box)(({ direction, light }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '4px 8px',
  borderRadius: '6px',
  backgroundColor: direction === 'Long'
    ? (light ? 'rgba(46, 204, 113, 0.1)' : 'rgba(46, 204, 113, 0.2)')
    : (light ? 'rgba(231, 76, 60, 0.1)' : 'rgba(231, 76, 60, 0.2)'),
  border: `1px solid ${direction === 'Long' ? '#2ecc71' : '#e74c3c'}`,
  color: direction === 'Long' ? '#2ecc71' : '#e74c3c',
  fontWeight: 'bold',
}));

export default function TradesTable() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { trades, currentPrice, loading, error, tradeImage, tradeImageLoading } = useSelector((s) => s.aiTrading);
  const { light } = useSelector((s) => s.theme);

  const [activeTab, setActiveTab] = useState('active');

  // ===== share popup state =====
  const [openShare, setOpenShare] = useState(false);
  const [shareError, setShareError] = useState('');
  const normalizedTradeImage = useMemo(() => normalizeImageUri(tradeImage), [tradeImage]);
  const hasValidImage = !!normalizedTradeImage;

  // ===== confirm dialog state =====
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: '',      // 'stop' | 'cancel'
    tradeId: null,
  });

  const openConfirmDialog = (type, tradeId) => {
    setConfirmDialog({ open: true, type, tradeId });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, type: '', tradeId: null });
  };

  // ===== logic =====
  const calculateProfitLoss = (trade, cp) => {
    if (!cp || !trade.entry_price || !trade.trade_direction) return 0;
    const priceDiff = (cp.price - trade.entry_price) / trade.entry_price;
    const adj = trade.trade_direction === 'Long' ? priceDiff : -priceDiff;
    return parseFloat((adj * trade.investment * trade.leverage).toFixed(2));
  };

  // Stop trade (confirmed)
  const handleStopTradeConfirmed = async () => {
    const { tradeId } = confirmDialog;
    closeConfirmDialog();
    try {
      const action = await dispatch(stopTrade(tradeId));
      if (action.meta.requestStatus === 'fulfilled') {
        enqueueSnackbar('Trade stopped successfully', { variant: 'success' });
        setTimeout(() => dispatch(fetchAIBalances()), 400);
      } else {
        enqueueSnackbar('Failed to stop trade', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('Error stopping trade', { variant: 'error' });
    }
  };

  // Cancel remaining (confirmed)
  const handleCancelConfirmed = async () => {
    const { tradeId } = confirmDialog;
    closeConfirmDialog();
    try {
      const action = await dispatch(cancelRemainingTrades(tradeId));
      if (action.meta.requestStatus === 'fulfilled') {
        enqueueSnackbar('Remaining trades cancelled successfully', { variant: 'success' });
      } else {
        enqueueSnackbar('Failed to cancel remaining trades', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('Error cancelling remaining trades', { variant: 'error' });
    }
  };

  const activeTrades = useMemo(
    () => trades.filter((t) => t.status === 'Active').sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [trades]
  );
  const historyTrades = useMemo(
    () => trades.filter((t) => (t.status === 'Completed' || t.status === 'Stopped'))
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [trades]
  );

  // ===== share popup handlers =====
  const openSharePopup = useCallback(async (tradeId) => {
    setShareError('');
    try {
      await dispatch(fetchTradeImage(tradeId)).unwrap();
      setOpenShare(true);
    } catch (e) {
      setShareError(String(e?.message || e));
      setOpenShare(true);
    }
  }, [dispatch]);

  const closeSharePopup = () => {
    setOpenShare(false);
    setShareError('');
    dispatch(clearTradeImage());
  };

  const downloadImage = async () => {
    try {
      if (!hasValidImage) return;

      let blob;
      if (looksLikeDataUri(normalizedTradeImage)) {
        const base64 = normalizedTradeImage.split(',')[1];
        const byteChars = atob(base64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
        const byteArray = new Uint8Array(byteNumbers);
        blob = new Blob([byteArray], { type: 'image/png' });
      } else {
        const res = await fetch(normalizedTradeImage);
        blob = await res.blob();
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trade_${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      enqueueSnackbar('Image downloaded', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(`Failed to download image: ${String(err?.message || err)}`, { variant: 'error' });
    }
  };

  const webShareImage = async () => {
    try {
      if (!hasValidImage) return;

      if (navigator.canShare && navigator.share) {
        let blob;
        if (looksLikeDataUri(normalizedTradeImage)) {
          const base64 = normalizedTradeImage.split(',')[1];
          const byteChars = atob(base64);
          const byteNumbers = new Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
          const byteArray = new Uint8Array(byteNumbers);
          blob = new Blob([byteArray], { type: 'image/png' });
        } else {
          const res = await fetch(normalizedTradeImage);
          blob = await res.blob();
        }

        const file = new File([blob], `trade_${Date.now()}.png`, { type: 'image/png' });

        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: 'Share Trade',
            text: 'Check out this trade snapshot.',
            files: [file]
          });
          enqueueSnackbar('Image shared', { variant: 'success' });
          return;
        }
      }

      await navigator.clipboard.writeText(normalizedTradeImage);
      enqueueSnackbar('Link copied to clipboard', { variant: 'info' });
    } catch (err) {
      enqueueSnackbar(`Failed to share: ${String(err?.message || err)}`, { variant: 'error' });
    }
  };

  // ===== card renderer =====
  const renderTradeCard = (trade) => {
    const pl = trade.status === 'Active' && currentPrice ? calculateProfitLoss(trade, currentPrice) : trade.profit_loss;
    const isProfit = pl > 0;

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={trade._id}>
        <Card sx={{
          bgcolor: light ? '#fff' : '#1e2024', borderRadius: 3,
          border: `1px solid ${light ? '#e0e0e0' : '#333'}`,
          backgroundImage: light ? 'linear-gradient(145deg, #ffffff, #f7f8fc)' : 'linear-gradient(145deg, #1e2024, #25272b)',
          position: 'relative'
        }}>
          <CardContent sx={{ p: 2.5 }}>
            {/* Header: TradeID + Share */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ color: light ? 'text.secondary' : '#b0b0b0' }}>
                Trade ID: {String(trade._id).slice(-6)}
              </Typography>

              <Tooltip title="Share image">
                <span>
                  <IconButton onClick={() => openSharePopup(trade._id)} size="small">
                    <ShareIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Typography variant="body1" sx={{ fontWeight: 'bold', color: light ? '#1a1a1a' : '#e0e0e0', mb: 1.5 }}>
              {String(trade.strategy)}
            </Typography>
            <Typography variant="body2" sx={{ color: light ? 'text.secondary' : '#b0b0b0', mb: 0.5 }}>
              Investment: {fmtSmart(trade.investment)} USDT
            </Typography>
            <Typography variant="body2" sx={{ color: light ? 'text.secondary' : '#b0b0b0', mb: 0.5 }}>
              Leverage: {String(trade.leverage)}x
            </Typography>
            <Typography variant="body2" sx={{ color: light ? 'text.secondary' : '#b0b0b0', mb: 0.5 }}>
              Entry: {String(trade.formatted_entry_price)}
            </Typography>
            <Typography variant="body2" sx={{ color: light ? 'text.secondary' : '#b0b0b0', mb: 0.5 }}>
              Predicted: {String(trade.formatted_predicted_price)}
            </Typography>

            <DirectionBox direction={trade.trade_direction} light={light}>
              {trade.trade_direction === 'Long'
                ? <ArrowUpward sx={{ color: '#2ecc71', mr: 0.5, fontSize: '1rem' }} />
                : <ArrowDownward sx={{ color: '#e74c3c', mr: 0.5, fontSize: '1rem' }} />}
              <Typography variant="body2">{trade.trade_direction}</Typography>
            </DirectionBox>

            {trade.status === 'Active' && trade.trade_type === 'Manual' && trade.remaining_trades > 0 && (
              <Typography variant="body2" sx={{ color: light ? 'text.secondary' : '#b0b0b0', mb: 1, mt: 1 }}>
                Remaining Trades: {String(trade.remaining_trades)}
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, mt: 1 }}>
              {isProfit ? <ProfitIcon sx={{ color: '#2ecc71', mr: 0.5 }} fontSize="small" />
                        : <LossIcon sx={{ color: '#e74c3c', mr: 0.5 }} fontSize="small" />}
              <Typography variant="body1" sx={{ color: isProfit ? '#2ecc71' : '#e74c3c', fontWeight: 'bold' }}>
                P/L: ${fmtSmart(pl)}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: light ? 'text.secondary' : '#b0b0b0' }}>
              Status: {trade.status}
            </Typography>

            {trade.status !== 'Active' && (
              <>
                {(typeof trade.tax_rate === 'number' || typeof trade.tax_amount === 'number') && (
                  <Typography variant="body2" sx={{ color: light ? 'text.secondary' : '#b0b0b0', mt: 0.5 }}>
                    Tax: {(Number(trade.tax_rate || 0) * 100).toFixed(0)}% (${fmtSmart(trade.tax_amount || 0)})
                  </Typography>
                )}
                {typeof trade.net_profit === 'number' && (
                  <Typography
                    variant="body2"
                    sx={{ color: Number(trade.net_profit) >= 0 ? '#2ecc71' : '#e74c3c', fontWeight: 'bold', mt: 0.5 }}
                  >
                    Net Profit: ${fmtSmart(trade.net_profit)}
                  </Typography>
                )}
              </>
            )}

            <Typography variant="body2" sx={{ color: light ? 'text.secondary' : '#b0b0b0', mt: 1 }}>
              Created: {new Date(trade.created_at).toLocaleString()}
            </Typography>

            {trade.status === 'Active' && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  sx={{
                    width: '100%', borderColor: '#e74c3c', color: '#e74c3c', textTransform: 'none',
                    '&:hover': { bgcolor: '#e74c3c', color: '#fff', borderColor: '#e74c3c' },
                  }}
                  size="small"
                  onClick={() => openConfirmDialog('stop', trade._id)}
                  disabled={loading}
                >
                  Stop Trade
                </Button>

                {trade.trade_type === 'Manual' && trade.remaining_trades > 0 && (
                  <Button
                    variant="outlined"
                    sx={{
                      width: '100%', borderColor: '#ff9800', color: '#ff9800', textTransform: 'none',
                      '&:hover': { bgcolor: '#ff9800', color: '#fff', borderColor: '#ff9800' },
                    }}
                    size="small"
                    onClick={() => openConfirmDialog('cancel', trade._id)}
                    disabled={loading}
                  >
                    Cancel Remaining Trades
                  </Button>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {error && (
        <Typography color="error" sx={{ mb: 2, fontSize: '0.9rem' }} role="alert">
          {String(error)}
        </Typography>
      )}

      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{
          mb: 3, bgcolor: light ? '#fff' : '#1e2024', borderRadius: 2,
          '.MuiTabs-indicator': { bgcolor: '#007bff', height: 3 },
          '.MuiTab-root': {
            color: light ? '#1a1a1a' : '#e0e0e0', textTransform: 'none',
            '&.Mui-selected': { color: '#e74c3c', fontWeight: 'bold' },
          },
        }}
      >
        <Tab label={`Active Trades (${activeTrades.length})`} value="active" />
        <Tab label="Trade History" value="history" />
      </Tabs>

      {activeTab === 'active' && (
        <ScrollableContainer light={light}>
          <Grid container spacing={2}>
            {activeTrades.length === 0
              ? <Typography variant="body1" align="center" sx={{ width: '100%', color: light ? '#1a1a1a' : '#e0e0e0', py: 4 }}>
                  No active trades available
                </Typography>
              : activeTrades.map(renderTradeCard)}
          </Grid>
        </ScrollableContainer>
      )}

      {activeTab === 'history' && (
        <ScrollableContainer light={light}>
          <Grid container spacing={2}>
            {historyTrades.length === 0
              ? <Typography variant="body1" align="center" sx={{ width: '100%', color: light ? '#1a1a1a' : '#e0e0e0', py: 4 }}>
                  No trade history available
                </Typography>
              : historyTrades.map(renderTradeCard)}
          </Grid>
        </ScrollableContainer>
      )}

      {/* ===== Share Popup (Dialog) ===== */}
      <Dialog
        open={openShare}
        onClose={closeSharePopup}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: light ? '#fff' : '#1e2024',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Share Trade
          </Typography>
          <IconButton onClick={closeSharePopup}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {tradeImageLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : hasValidImage ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img
                alt="Trade snapshot"
                src={normalizedTradeImage}
                style={{
                  maxWidth: '100%',
                  maxHeight: '60vh',
                  borderRadius: 12,
                  border: `1px solid ${light ? '#e0e0e0' : '#333'}`
                }}
              />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>Failed to load image</Typography>
              {shareError && <Typography variant="body2" color="error">{shareError}</Typography>}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            startIcon={<DownloadIcon />}
            variant="outlined"
            onClick={downloadImage}
            disabled={!hasValidImage || tradeImageLoading}
            sx={{ textTransform: 'none' }}
          >
            Download
          </Button>
          <Button
            startIcon={<ShareIcon />}
            variant="contained"
            onClick={webShareImage}
            disabled={!hasValidImage || tradeImageLoading}
            sx={{ textTransform: 'none', bgcolor: '#16C784', '&:hover': { bgcolor: '#14b072' } }}
          >
            Share
          </Button>
        </DialogActions>
      </Dialog>
      {/* ===== End Share Popup ===== */}

      {/* ===== Confirm Action Popup ===== */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: light ? '#fff' : '#1e2024',
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>
          {confirmDialog.type === 'stop'
            ? 'Confirm Stop Trade'
            : 'Confirm Cancel Remaining Trades'}
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" align="center" sx={{ color: light ? '#333' : '#ddd' }}>
            {confirmDialog.type === 'stop'
              ? 'Are you sure you want to stop this trade?'
              : 'Are you sure you want to cancel all remaining trades? This action cannot be undone.'}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', py: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={closeConfirmDialog}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={
              confirmDialog.type === 'stop'
                ? handleStopTradeConfirmed
                : handleCancelConfirmed
            }
            sx={{
              bgcolor: confirmDialog.type === 'stop' ? '#e74c3c' : '#ff9800',
              '&:hover': { opacity: 0.9 },
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Yes, Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {/* ===== End Confirm Action Popup ===== */}
    </Box>
  );
}
